import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { Notice } from '../types';

interface NoticeContextType {
  notices: Notice[];           // 공개된 공지만 (일반 사용자용)
  allNotices: Notice[];        // 전체 공지 (관리자용)
  loading: boolean;
  error: string | null;
  addNotice: (notice: Omit<Notice, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateNotice: (id: number, notice: Partial<Notice>) => Promise<void>;
  deleteNotice: (id: number) => Promise<void>;
  getNotice: (id: number) => Notice | undefined;
  fetchNoticeContent: (id: number) => Promise<string>;
  incrementViews: (id: number) => Promise<void>;
  refreshNotices: () => Promise<void>;
  ensureLoaded: () => void;
}

const NoticeContext = createContext<NoticeContextType | undefined>(undefined);

export const NoticeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notices, setNotices] = useState<Notice[]>([]);           // 공개된 공지만
  const [allNotices, setAllNotices] = useState<Notice[]>([]);     // 전체 공지
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Supabase에서 공지사항 데이터 가져오기
  const fetchNotices = async () => {
    try {
      setLoading(true);
      setError(null);

      // content(본문 HTML)는 목록에 필요 없고, 이미지가 섞이면 목록 응답을 크게 부풀린다.
      // 본문은 상세/수정 화면에서 단건으로 읽는다(fetchNoticeContent).
      const { data, error: fetchError } = await supabase
        .from('notices')
        .select('id,title,department,author,view_count,is_important,notice_end_date,is_published,file_url,file_name,file_size,created_at,updated_at')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      console.log('📥 Fetched notices from DB:', data);

      // 공지글 정렬 로직:
      // 1. is_important가 true이고 notice_end_date가 미래 또는 null인 경우 → 상단 고정
      // 2. 나머지는 created_at 기준 최신순
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const sortNotices = (noticeList: Notice[]) => {
        return noticeList.sort((a, b) => {
          const aIsActive = a.is_important && (!a.notice_end_date || new Date(a.notice_end_date) >= now);
          const bIsActive = b.is_important && (!b.notice_end_date || new Date(b.notice_end_date) >= now);

          // 둘 다 활성 공지 또는 둘 다 일반 글인 경우: created_at 기준 정렬
          if (aIsActive === bIsActive) {
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          }

          // 활성 공지가 상단에 오도록
          return aIsActive ? -1 : 1;
        });
      };

      // content는 목록에서 제외했으므로 빈 문자열로 채운다(상세·수정 화면이 단건으로 읽어감)
      const allData: Notice[] = (data || []).map((notice: any) => ({ ...notice, content: '' }));

      // 전체 공지 (관리자용)
      setAllNotices(sortNotices([...allData]));

      // 공개된 공지만 (일반 사용자용)
      const publishedData = allData.filter(notice => notice.is_published !== false);
      setNotices(sortNotices(publishedData));
    } catch (err) {
      console.error('Error fetching notices:', err);
      setError(err instanceof Error ? err.message : '공지사항을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 지연 로드: 앱 부팅 시 모든 Provider가 전체 테이블을 읽으면 방문자 1명당 요청 수십 건이 발생해
  // 접속이 몰릴 때 DB가 포화된다. 이 데이터를 쓰는 화면이 뜰 때만 한 번 읽는다.
  const loadedRef = useRef(false);
  const ensureLoaded = useCallback(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchNotices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addNotice = async (noticeData: Omit<Notice, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => {
    try {
      setError(null);

      const { error: insertError } = await supabase
        .from('notices')
        .insert({
          title: noticeData.title,
          department: noticeData.department,
          content: noticeData.content,
          is_important: noticeData.is_important || false,
          notice_end_date: noticeData.notice_end_date || null,
          is_published: noticeData.is_published ?? true,
          author: noticeData.author || '관리자',
          file_url: noticeData.file_url || null,
          file_name: noticeData.file_name || null,
          file_size: noticeData.file_size || null,
          view_count: 0
        });

      if (insertError) throw insertError;

      // 데이터 다시 불러오기
      await fetchNotices();
    } catch (err) {
      console.error('Error adding notice:', err);
      setError(err instanceof Error ? err.message : '공지사항 추가에 실패했습니다.');
      throw err;
    }
  };

  const updateNotice = async (id: number, noticeData: Partial<Notice>) => {
    try {
      setError(null);

      const updateData: any = {};
      if (noticeData.title) updateData.title = noticeData.title;
      if (noticeData.department) updateData.department = noticeData.department;
      if (noticeData.content) updateData.content = noticeData.content;
      if (noticeData.is_important !== undefined) updateData.is_important = noticeData.is_important;
      if (noticeData.notice_end_date !== undefined) updateData.notice_end_date = noticeData.notice_end_date;
      if (noticeData.is_published !== undefined) updateData.is_published = noticeData.is_published;
      if (noticeData.author) updateData.author = noticeData.author;
      // 파일 관련 필드 (null도 허용해야 파일 삭제 가능)
      if (noticeData.file_url !== undefined) updateData.file_url = noticeData.file_url;
      if (noticeData.file_name !== undefined) updateData.file_name = noticeData.file_name;
      if (noticeData.file_size !== undefined) updateData.file_size = noticeData.file_size;

      console.log('🔍 Update Notice - noticeData:', noticeData);
      console.log('🔍 Update Notice - updateData:', updateData);

      const { error: updateError } = await supabase
        .from('notices')
        .update(updateData)
        .eq('id', id);

      if (updateError) throw updateError;

      // 데이터 다시 불러오기
      await fetchNotices();
    } catch (err) {
      console.error('Error updating notice:', err);
      setError(err instanceof Error ? err.message : '공지사항 수정에 실패했습니다.');
      throw err;
    }
  };

  const deleteNotice = async (id: number) => {
    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('notices')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // 로컬 상태에서 제거
      setNotices(prev => prev.filter(notice => notice.id !== id));
    } catch (err) {
      console.error('Error deleting notice:', err);
      setError(err instanceof Error ? err.message : '공지사항 삭제에 실패했습니다.');
      throw err;
    }
  };

  const incrementViews = async (id: number) => {
    try {
      // view_count를 직접 증가
      const notice = notices.find(n => n.id === id);
      if (notice) {
        const { error: updateError } = await supabase
          .from('notices')
          .update({ view_count: notice.view_count + 1 })
          .eq('id', id);

        if (updateError) throw updateError;

        // 로컬 상태 업데이트
        setNotices(prev => prev.map(n =>
          n.id === id ? { ...n, view_count: n.view_count + 1 } : n
        ));
      }
    } catch (err) {
      console.error('Error incrementing views:', err);
    }
  };

  const getNotice = (id: number) => {
    return allNotices.find(notice => notice.id === id);
  };

  // 본문(content)은 목록 조회에서 제외되므로 상세·수정 화면에서 단건으로 읽는다.
  const fetchNoticeContent = async (id: number): Promise<string> => {
    const { data, error: contentError } = await supabase
      .from('notices')
      .select('content')
      .eq('id', id)
      .maybeSingle();

    if (contentError) {
      console.error('Error fetching notice content:', contentError);
      return '';
    }
    return data?.content || '';
  };

  const refreshNotices = async () => {
    await fetchNotices();
  };

  return (
    <NoticeContext.Provider value={{
      ensureLoaded,
      notices,
      allNotices,
      loading,
      error,
      addNotice,
      updateNotice,
      deleteNotice,
      getNotice,
      fetchNoticeContent,
      incrementViews,
      refreshNotices
    }}>
      {children}
    </NoticeContext.Provider>
  );
};

export const useNotices = () => {
  const context = useContext(NoticeContext);
  // 이 훅을 쓰는 화면이 실제로 떴을 때만 조회를 시작한다(지연 로드).
  const ensureLoaded = context?.ensureLoaded;
  useEffect(() => {
    ensureLoaded?.();
  }, [ensureLoaded]);

  if (!context) {
    throw new Error('useNotices must be used within a NoticeProvider');
  }
  return context;
};
